import { useState } from "react";
import {
  AlertTriangle,
  ChevronRight,
  ClipboardCheck,
  RefreshCcw,
  Undo2,
} from "lucide-react";
import type { CurrentUser } from "@/features/auth/currentUserTypes";
import { todaySaoPaulo } from "@/lib/dates";
import { useSupplierCurrentLoads } from "@/features/dashboard/hooks";
import {
  formatDashboardCount,
  formatDashboardCurrency,
  formatDashboardPercent,
  formatDashboardUnitQuantity,
} from "@/features/dashboard/metricFormatters";
import type { DashboardPeriodState } from "@/features/dashboard/period";
import type {
  DashboardCountMetric,
  DashboardDetailKind,
  DashboardMoneyMetric,
  DashboardRankingKind,
  DashboardSectionName,
  SupplierDashboardAnalytics,
  SupplierDashboardOperations,
} from "@/features/dashboard/types";
import { SuperPeriod } from "./SuperPeriod";
import { SupplierTrend } from "./SupplierTrend";
import "./SuperDashboard.css";
import "./SupplierDashboard.css";

type ReturnTarget = {
  kind: Extract<DashboardRankingKind, "products" | "stores">;
  sort: "value";
};
export type SupplierDashboardPanelRequest =
  | {
      mode: "ranking";
      kind: ReturnTarget["kind"];
      sort: "value";
      title: string;
    }
  | {
      mode: "detail";
      kind: Extract<DashboardDetailKind, "product" | "store">;
      id: string;
      title: string;
      returnTo: ReturnTarget;
    }
  | { mode: "quantities" }
  | { mode: "quality" };

export interface SupplierDashboardProps {
  user: CurrentUser;
  analytics: SupplierDashboardAnalytics;
  operations?: SupplierDashboardOperations;
  period: DashboardPeriodState;
  pendingPeriod?: DashboardPeriodState | null;
  updating?: boolean;
  onPeriodChange: (period: DashboardPeriodState) => void;
  onNavigate: (path: "/pedidos" | "/cargas") => void;
  onOpenPanel: (request: SupplierDashboardPanelRequest) => void;
  onRetryAnalytics: () => void;
  onRetryOperations: () => void;
}

const amount = (cents: number) => formatDashboardCurrency(cents).full;
const count = (value: number) => formatDashboardCount(value).full;
const status = (
  analytics: SupplierDashboardAnalytics,
  section: DashboardSectionName,
) => analytics.section_status?.find((row) => row.section === section)?.status;
function Unavailable({
  error,
  retry,
}: {
  error?: boolean;
  retry?: () => void;
}) {
  return (
    <p className="lnk209-empty" role="status">
      {error
        ? "Falha ao carregar esta seção."
        : "Dados indisponíveis para esta seção."}
      {error && retry && (
        <>
          {" "}
          <button onClick={retry}>Tentar novamente</button>
        </>
      )}
    </p>
  );
}

function Kpi({
  label,
  metric,
  current,
  scope,
  description,
  onClick,
  testId,
}: {
  label: string;
  metric: DashboardMoneyMetric | DashboardCountMetric;
  current?: boolean;
  scope?: string;
  description?: string;
  onClick: () => void;
  testId?: string;
}) {
  const comparison =
    metric.status === "available" ? metric.comparison : undefined;
  return (
    <article>
      <span className="lnk209-kpi-index">{label.toUpperCase()}</span>
      <strong data-testid={testId}>
        {metric.status !== "available"
          ? "—"
          : metric.unit === "cents"
            ? amount(metric.value)
            : count(metric.value)}
      </strong>
      {current ? (
        <span className="lnk209-no-compare">operação atual</span>
      ) : comparison ? (
        <span
          className={`lnk209-delta ${comparison.direction === "down" ? "negative" : comparison.direction === "same" ? "neutral" : "positive"}`}
        >
          {comparison.direction === "down"
            ? "↘ −"
            : comparison.direction === "same"
              ? "→ "
              : "↗ +"}
          {formatDashboardPercent(comparison.percentage).full.replace(
            /^[+-]/,
            "",
          )}
        </span>
      ) : (
        <span className="lnk209-no-compare" aria-label="Sem base comparável">
          —
        </span>
      )}
      {metric.status !== "available" && (
        <span className="lnk209-kpi-unavailable">
          {metric.status === "error" ? "Falha nos dados" : "Dado indisponível"}
        </span>
      )}
      {scope && <small className="lnk209-supplier-kpi-scope">{scope}</small>}
      <button
        className="lnk209-kpi-link"
        aria-label={`Abrir ${label}${description ? `, ${description}` : scope ? `, ${scope}` : ""}`}
        title={description}
        onClick={onClick}
      />
    </article>
  );
}

function LoadDonut({
  analytics,
  retry,
}: {
  analytics: SupplierDashboardAnalytics;
  retry: () => void;
}) {
  const [active, setActive] = useState<string | null>(null);
  const situation = analytics.sections.load_situation;
  const availability = status(analytics, "load_situation");
  const states = (
    [
      { state: "concluded", label: "Entregues", color: "#327554" },
      { state: "in_progress", label: "Em carregamento", color: "#d5a145" },
      { state: "not_started", label: "Pendentes", color: "#a8b6a7" },
    ] as const
  ).map((item) => ({
    ...item,
    count: situation.states.find((row) => row.state === item.state)?.count ?? 0,
  }));
  let offset = 0;
  return (
    <section
      className="lnk209-panel lnk209-load"
      aria-label="Situação das cargas"
    >
      <header>
        <div>
          <h2>Situação das cargas</h2>
        </div>
        <span className="lnk209-panel-meta">por quantidade</span>
      </header>
      {availability && availability !== "available" ? (
        <Unavailable error={availability === "error"} retry={retry} />
      ) : situation.total ? (
        <>
          <div className="lnk209-load-body">
            <div
              className="lnk209-donut"
              role="img"
              aria-label={`${situation.total} cargas: ${states.map((item) => `${item.count} ${item.label}`).join(", ")}`}
            >
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <circle
                  cx="60"
                  cy="60"
                  r="48"
                  fill="none"
                  stroke="var(--track)"
                  strokeWidth="14"
                />
                {states.map((item) => {
                  const start = offset;
                  const slice = (item.count / situation.total) * 100;
                  offset += slice;
                  return (
                    <circle
                      key={item.state}
                      cx="60"
                      cy="60"
                      r="48"
                      fill="none"
                      stroke={item.color}
                      strokeWidth="14"
                      pathLength="100"
                      strokeDasharray={`${slice} ${100 - slice}`}
                      strokeDashoffset={-start}
                      transform="rotate(-90 60 60)"
                    />
                  );
                })}
              </svg>
              <div>
                <strong>{count(situation.total)}</strong>
                <small>CARGAS</small>
              </div>
            </div>
            <div className="lnk209-legend">
              {states.map((item) => (
                <button
                  key={item.state}
                  aria-label={item.label}
                  aria-pressed={active === item.label}
                  onMouseEnter={() => setActive(item.label)}
                  onFocus={() => setActive(item.label)}
                  onClick={() => setActive(item.label)}
                >
                  <i style={{ background: item.color }} />
                  <span>{item.label}</span>
                  <strong>{count(item.count)}</strong>
                </button>
              ))}
            </div>
          </div>
          <p className="lnk209-detail" role="status">
            {active
              ? `${active}: ${count(states.find((item) => item.label === active)!.count)} cargas`
              : "Selecione um estado para conferir a quantidade"}
          </p>
        </>
      ) : (
        <p className="lnk209-empty">
          Nenhuma carga registrada neste período. Explore outra data.
        </p>
      )}
    </section>
  );
}

type RankRow = { id?: string; name: string; value: string };
function Ranking({
  title,
  rows,
  kind,
  analytics,
  retry,
  onOpen,
}: {
  title: string;
  rows: RankRow[];
  kind: ReturnTarget["kind"];
  analytics: SupplierDashboardAnalytics;
  retry: () => void;
  onOpen: SupplierDashboardProps["onOpenPanel"];
}) {
  const availability = status(
    analytics,
    kind === "products" ? "leading_products" : "stores_served",
  );
  const blocked = availability && availability !== "available";
  const open = (row?: RankRow) => {
    if (!row?.id)
      return onOpen({ mode: "ranking", kind, sort: "value", title });
    onOpen({
      mode: "detail",
      kind: kind === "products" ? "product" : "store",
      id: row.id,
      title: row.name,
      returnTo: { kind, sort: "value" },
    });
  };
  return (
    <section className="lnk209-panel lnk209-ranking" aria-label={title}>
      <header>
        <button
          className="lnk209-ranking-heading"
          disabled={Boolean(blocked)}
          onClick={() => open()}
        >
          <h2>{title}</h2>
        </button>
        <button
          className="lnk209-view-all"
          disabled={Boolean(blocked)}
          onClick={() => open()}
        >
          Ver mais <ChevronRight size={15} aria-hidden="true" />
        </button>
      </header>
      {blocked ? (
        <Unavailable error={availability === "error"} retry={retry} />
      ) : rows.length ? (
        <div className="lnk209-ranking-list">
          {rows.slice(0, 3).map((row, index) => (
            <button
              key={`${row.id ?? row.name}-${index}`}
              aria-label={`${row.name}, ${row.value}`}
              onClick={() => open(row)}
            >
              <span className="lnk209-rank">
                {String(index + 1).padStart(2, "0")}
              </span>
              <span className="lnk209-ranking-name">
                <strong>{row.name}</strong>
              </span>
              <strong className="lnk209-ranking-value">{row.value}</strong>
            </button>
          ))}
        </div>
      ) : (
        <p className="lnk209-empty">Sem dados observados neste período.</p>
      )}
    </section>
  );
}

const unitLabel: Record<string, string> = {
  kg: "Peso avulso",
  cx: "Caixas",
  bdj: "Bandejas",
  sc: "Sacos",
  un: "Unidades",
};
function SupplierSections({
  analytics,
  retry,
  open,
}: {
  analytics: SupplierDashboardAnalytics;
  retry: () => void;
  open: SupplierDashboardProps["onOpenPanel"];
}) {
  const [activeOutcome, setActiveOutcome] = useState<string | null>(null);
  const [activeUnit, setActiveUnit] = useState<string | null>(null);
  const quality = analytics.sections.quality_outcomes;
  const outcomes = [
    {
      name: "Aguardando decisão",
      value: count(quality.decision_count),
      icon: ClipboardCheck,
    },
    {
      name: "Substituições",
      value: count(quality.substituted_count),
      icon: RefreshCcw,
    },
    { name: "Devoluções", value: count(quality.returned_count), icon: Undo2 },
    {
      name: "Impacto das devoluções",
      value: amount(quality.return_impact_cents),
      icon: AlertTriangle,
    },
  ];
  const qualityStatus = status(analytics, "quality_outcomes");
  const quantityStatus = status(analytics, "quantity_sold");
  return (
    <div className="lnk209-bottom lnk209-supplier-bottom">
      <section
        className="lnk209-panel lnk209-quality-preview"
        aria-label="Qualidade das entregas"
      >
        <header>
          <h2>Qualidade das entregas</h2>
        </header>
        {qualityStatus && qualityStatus !== "available" ? (
          <Unavailable error={qualityStatus === "error"} retry={retry} />
        ) : (
          <div className="lnk209-quality-ledger">
            {outcomes.map(({ name, value, icon: Icon }) => (
              <button
                key={name}
                aria-label={`${name}: ${value}`}
                aria-pressed={activeOutcome === name}
                onMouseEnter={() => setActiveOutcome(name)}
                onFocus={() => setActiveOutcome(name)}
                onClick={() => {
                  setActiveOutcome(name);
                  open({ mode: "quality" });
                }}
              >
                <span className="lnk209-outcome-name">
                  <Icon size={18} strokeWidth={1.8} aria-hidden="true" />
                  {name}
                </span>
                <strong>{value}</strong>
              </button>
            ))}
          </div>
        )}
      </section>
      <section className="lnk209-panel" aria-label="Quantidade vendida">
        <header>
          <h2>Quantidade vendida</h2>
        </header>
        {quantityStatus && quantityStatus !== "available" ? (
          <Unavailable error={quantityStatus === "error"} retry={retry} />
        ) : analytics.sections.quantity_sold.length ? (
          <div className="lnk209-unit-ledger">
            {analytics.sections.quantity_sold.map(({ unit, quantity }) => {
              const value = formatDashboardUnitQuantity(unit, quantity);
              return (
                <button
                  key={unit}
                  aria-label={`${unitLabel[unit] ?? unit}: ${value?.full ?? "dado indisponível"} ${unit}`}
                  aria-pressed={activeUnit === unit}
                  onFocus={() => setActiveUnit(unit)}
                  onMouseEnter={() => setActiveUnit(unit)}
                  onClick={() => {
                    setActiveUnit(unit);
                    open({ mode: "quantities" });
                  }}
                >
                  <span className="lnk209-unit-name">
                    {unitLabel[unit] ?? unit}
                    <small>{unit}</small>
                  </span>
                  <strong>{value?.full ?? "—"}</strong>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="lnk209-empty">Sem dados observados neste período.</p>
        )}
      </section>
    </div>
  );
}

function CurrentLoads({ user }: { user: CurrentUser }) {
  const query = useSupplierCurrentLoads(user);
  const pages = query.data?.pages ?? [];
  const loads = pages.flatMap((page) => page.items);
  const stale = pages.some((page) => page.projection_status === "stale");
  const unavailable = query.isError && !query.isFetchNextPageError;
  return (
    <section
      className="lnk209-panel lnk209-current-loads"
      aria-label="Cargas atuais"
    >
      <header>
        <h2>Cargas atuais</h2>
        {pages.length > 0 && !unavailable && (
          <span className="lnk209-panel-meta">
            {count(loads.length)}
            {query.hasNextPage ? "+" : ""} cargas
            <small>valores provisórios{stale ? " · desatualizado" : ""}</small>
          </span>
        )}
      </header>
      {unavailable ? (
        <p className="lnk209-empty" role="status">
          Cargas atuais indisponíveis no momento.{" "}
          <button onClick={() => query.refetch()}>Tentar novamente</button>
        </p>
      ) : query.isPending ? (
        <p className="lnk209-empty" role="status">
          Carregando cargas atuais…
        </p>
      ) : loads.length ? (
        <div className="lnk209-current-load-list">
          {loads.map((load) => {
            const state =
              load.state === "in_progress" ? "Em andamento" : "Não iniciada";
            const value = amount(load.value_cents);
            return (
              <div key={load.order_id}>
                <a
                  href="/cargas"
                  aria-label={`${load.label}, pedido ${load.order_id}, ${state}, ${value}, valor provisório`}
                >
                  <span>
                    <strong>{load.label}</strong>
                    <small>
                      Pedido #{load.order_id.slice(-8).toUpperCase()} · {state}
                    </small>
                  </span>
                  <strong>{value}</strong>
                  <ChevronRight size={16} aria-hidden="true" />
                </a>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="lnk209-empty">Nenhuma carga aberta neste momento.</p>
      )}
      {query.isFetchNextPageError && (
        <p className="lnk209-loads-more" role="status">
          Mais cargas indisponíveis no momento.
        </p>
      )}
      {query.hasNextPage && !unavailable && (
        <button
          className="lnk209-load-more"
          disabled={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          {query.isFetchingNextPage
            ? "Carregando…"
            : query.isFetchNextPageError
              ? "Tentar carregar mais"
              : "Carregar mais"}
        </button>
      )}
    </section>
  );
}

export function SupplierDashboard({
  user,
  analytics,
  operations,
  period,
  pendingPeriod,
  updating,
  onPeriodChange,
  onNavigate,
  onOpenPanel,
  onRetryAnalytics,
}: SupplierDashboardProps) {
  const openSection = operations?.section_status?.find(
    (row) => row.section === "open_loads",
  );
  const openValue: DashboardMoneyMetric =
    !operations ||
    operations.projection_status === "not_ready" ||
    (openSection && openSection.status !== "available")
      ? {
          status: openSection?.status === "error" ? "error" : "unavailable",
          unit: "cents",
          reason_code: "current_operations",
        }
      : {
          status: "available",
          unit: "cents",
          value: operations.open_loads.total_value_cents,
        };
  const context = new Date(`${todaySaoPaulo()}T12:00:00Z`);
  const trendStatus = status(analytics, "sales_evolution");
  const products = analytics.sections.leading_products.map((row) => ({
    id: row.id,
    name: row.name,
    value:
      row.quantities
        .filter(({ quantity }) => Number(quantity) !== 0)
        .map(
          ({ unit, quantity }) =>
            `${formatDashboardUnitQuantity(unit, quantity)?.full ?? "—"} ${unit}`,
        )
        .join(" · ") || "—",
  }));
  const stores = analytics.sections.stores_served.map((row) => ({
    id: row.id,
    name: row.name,
    value: amount(row.value_cents),
  }));
  return (
    <main
      className="lnk209-super lnk209-supplier lnk209-app"
      data-testid="supplier-dashboard"
      data-dashboard-role={user.role}
      aria-busy={updating || undefined}
    >
      <div className="lnk209-content">
        <div className="lnk209-inner">
          <header className="lnk209-main-head">
            <div>
              <h1>Painel do fornecedor</h1>
            </div>
            <span className="lnk209-header-context">
              <span>
                {period.period === "custom"
                  ? "PERÍODO PERSONALIZADO"
                  : context
                      .toLocaleDateString("pt-BR", {
                        weekday: "long",
                        timeZone: "UTC",
                      })
                      .toUpperCase()}
              </span>
              <strong>
                {period.period === "custom" && period.from && period.to
                  ? `${period.from.slice(8, 10)}/${period.from.slice(5, 7)}–${period.to.slice(8, 10)}/${period.to.slice(5, 7)}/${period.to.slice(0, 4)}`
                  : context
                      .toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        timeZone: "UTC",
                      })
                      .toUpperCase()
                      .replaceAll(".", "")}
              </strong>
            </span>
          </header>
          <SuperPeriod
            period={period}
            pendingPeriod={pendingPeriod}
            metadata={analytics.period}
            updating={updating}
            onChange={onPeriodChange}
          />
          <section
            className="lnk209-kpis"
            aria-label="Indicadores do fornecedor"
            data-testid="supplier-dashboard-headlines"
          >
            <Kpi
              label="Vendas no período"
              metric={analytics.metrics.earnings}
              scope="itens recebidos"
              description="Quantidades recebidas no período, mesmo com a carga em andamento."
              testId="supplier-sales"
              onClick={() => onNavigate("/pedidos")}
            />
            <Kpi
              label="Valor em aberto"
              metric={openValue}
              description="Quantidades ainda aguardadas, independentemente do período selecionado."
              current
              onClick={() => onNavigate("/cargas")}
            />
            <Kpi
              label="Pedidos fornecidos"
              metric={analytics.metrics.orders}
              onClick={() => onNavigate("/pedidos")}
            />
            <Kpi
              label="Ticket médio"
              metric={analytics.metrics.average_ticket}
              onClick={() => onNavigate("/pedidos")}
            />
          </section>
          <div
            className="lnk209-analysis"
            data-testid="supplier-dashboard-analysis"
          >
            {trendStatus && trendStatus !== "available" ? (
              <section className="lnk209-panel" aria-label="Evolução de vendas">
                <header>
                  <h2>Evolução de vendas</h2>
                </header>
                <Unavailable
                  error={trendStatus === "error"}
                  retry={onRetryAnalytics}
                />
              </section>
            ) : (
              <SupplierTrend
                points={analytics.sections.sales_evolution}
                period={period}
                metadata={analytics.period}
                onHistoryChange={(history) =>
                  onPeriodChange({ ...period, history })
                }
              />
            )}
            <LoadDonut analytics={analytics} retry={onRetryAnalytics} />
          </div>
          <div
            className="lnk209-rankings"
            data-testid="supplier-dashboard-rankings"
          >
            <Ranking
              title="Produtos em destaque"
              kind="products"
              rows={products}
              analytics={analytics}
              retry={onRetryAnalytics}
              onOpen={onOpenPanel}
            />
            <Ranking
              title="Lojas atendidas"
              kind="stores"
              rows={stores}
              analytics={analytics}
              retry={onRetryAnalytics}
              onOpen={onOpenPanel}
            />
          </div>
          <SupplierSections
            analytics={analytics}
            retry={onRetryAnalytics}
            open={onOpenPanel}
          />
          <CurrentLoads user={user} />
        </div>
      </div>
    </main>
  );
}
